import { useMemo, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PmCard, PmInfoBox, PmViewToggle } from '../../../../components/pm';
import {
  buildRibbons, type ClosedBoardBar, type LegendEntry,
} from './aggregateClosedBoards';

export type ChartMode = 'single' | 'flow';

export const CHART_MODE_STORAGE_KEY = 'phq.fortschritt.geschlosseneBoards.modus';
/** Abstand zwischen den Balken im Modus „Einzelbalken" (px). */
export const SINGLE_BAR_GAP = 3;

const SINGLE_BAR_WIDTH = 48;
/** Fließbalken: Balken und Schrägen-Zone je halbe Slot-Breite, Balken stoßen ohne Lücke aneinander. */
const FLOW_BAR_WIDTH = 28;
const FLOW_RIBBON_WIDTH = 28;
const PLOT_HEIGHT = 260;
const PAD_LEFT = 40;
const PAD_RIGHT = 16;
const PAD_TOP = 16;
const PAD_BOTTOM = 52;

function readMode(): ChartMode {
  try {
    return localStorage.getItem(CHART_MODE_STORAGE_KEY) === 'flow' ? 'flow' : 'single';
  } catch {
    return 'single';
  }
}

function writeMode(mode: ChartMode): void {
  try {
    localStorage.setItem(CHART_MODE_STORAGE_KEY, mode);
  } catch {
    /* Speicher gesperrt — Wahl gilt nur für diese Sitzung */
  }
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

interface Props {
  bars: ClosedBoardBar[];
  legend: LegendEntry[];
}

/** Balkendiagramm: ein Balken je geschlossenem Kanban-Board, nach Arbeitspaket gestapelt (PHQ-112). */
export function ClosedBoardsChart({ bars, legend }: Props) {
  const { t } = useTranslation('projects');
  const [mode, setMode] = useState<ChartMode>(readMode);

  const changeMode = (next: ChartMode) => {
    setMode(next);
    writeMode(next);
  };

  const ribbons = useMemo(() => (mode === 'flow' ? buildRibbons(bars) : []), [bars, mode]);

  const title = t('fortschritt.boardsTitle', 'Erledigte Karten je geschlossenem Board');

  if (bars.length === 0) {
    return (
      <PmInfoBox variant="info" title={title}>
        {t('fortschritt.boardsEmpty', 'Noch kein Kanban-Board geschlossen. Sobald ein Board über „Neues Board“ eingefroren wird, erscheint hier sein Balken.')}
      </PmInfoBox>
    );
  }

  const barWidth = mode === 'flow' ? FLOW_BAR_WIDTH : SINGLE_BAR_WIDTH;
  const step = mode === 'flow' ? FLOW_BAR_WIDTH + FLOW_RIBBON_WIDTH : SINGLE_BAR_WIDTH + SINGLE_BAR_GAP;
  const maxTotal = Math.max(...bars.map((b) => b.total), 1);
  const plotWidth = (bars.length - 1) * step + barWidth;
  const width = PAD_LEFT + plotWidth + PAD_RIGHT;
  const height = PAD_TOP + PLOT_HEIGHT + PAD_BOTTOM;
  const baseY = PAD_TOP + PLOT_HEIGHT;
  const xOf = (i: number) => PAD_LEFT + i * step;
  const yOf = (value: number) => baseY - (value / maxTotal) * PLOT_HEIGHT;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((p) => Math.round(maxTotal * p)).filter((v, i, a) => a.indexOf(v) === i);
  const colorLabel = (apKey: string) => legend.find((l) => l.apKey === apKey)?.label ?? '';

  return (
    <PmCard>
      <Stack spacing={1.5}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
          <PmViewToggle<ChartMode>
            value={mode}
            onChange={changeMode}
            options={[
              { value: 'single', label: t('fortschritt.modeSingle', 'Einzelbalken'), icon: <Typography sx={{ fontSize: '0.72rem', fontWeight: 700 }}>{t('fortschritt.modeSingle', 'Einzelbalken')}</Typography> },
              { value: 'flow', label: t('fortschritt.modeFlow', 'Fließbalken'), icon: <Typography sx={{ fontSize: '0.72rem', fontWeight: 700 }}>{t('fortschritt.modeFlow', 'Fließbalken')}</Typography> },
            ]}
          />
        </Box>
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <svg
            width={width}
            height={height}
            role="img"
            aria-label={title}
            data-testid="fb-chart"
            data-mode={mode}
          >
            {ticks.map((tick) => (
              <g key={tick}>
                <line x1={PAD_LEFT} y1={yOf(tick)} x2={width - PAD_RIGHT} y2={yOf(tick)} stroke="var(--pm-bg-deep)" strokeDasharray="2,4" />
                <text x={PAD_LEFT - 8} y={yOf(tick) + 4} fontSize="11" textAnchor="end" fill="var(--pm-fg-subtle)">{tick}</text>
              </g>
            ))}
            {ribbons.map((r) => (
              <polygon
                key={`${r.fromIndex}-${r.apKey}`}
                data-testid="fb-ribbon"
                fill={r.color}
                opacity={0.35}
                points={[
                  [xOf(r.fromIndex) + barWidth, yOf(r.from.top)],
                  [xOf(r.toIndex), yOf(r.to.top)],
                  [xOf(r.toIndex), yOf(r.to.bottom)],
                  [xOf(r.fromIndex) + barWidth, yOf(r.from.bottom)],
                ].map(([x, y]) => `${x},${y}`).join(' ')}
              />
            ))}
            {bars.map((bar, i) => (
              <g key={bar.key}>
                {bar.segments.map((s) => (
                  <rect
                    key={s.apKey}
                    data-testid="fb-seg"
                    data-bar={i}
                    data-ap={s.apKey}
                    x={xOf(i)}
                    y={yOf(s.top)}
                    width={barWidth}
                    height={yOf(s.bottom) - yOf(s.top)}
                    fill={s.color}
                  >
                    <title>{`${bar.label} · ${colorLabel(s.apKey)}: ${s.count}`}</title>
                  </rect>
                ))}
                <text x={xOf(i) + barWidth / 2} y={yOf(bar.total) - 4} fontSize="11" textAnchor="middle" fill="var(--pm-fg-muted)">{bar.total}</text>
                <text x={xOf(i) + barWidth / 2} y={baseY + 16} fontSize="10" textAnchor="middle" fill="var(--pm-fg-subtle)">
                  {bar.label.length > 9 ? `${bar.label.slice(0, 8)}…` : bar.label}
                  <title>{bar.label}</title>
                </text>
                <text x={xOf(i) + barWidth / 2} y={baseY + 30} fontSize="10" textAnchor="middle" fill="var(--pm-fg-subtle)">{formatDate(bar.closedAt)}</text>
              </g>
            ))}
          </svg>
        </Box>
        <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" sx={{ fontSize: '0.78rem', color: 'var(--pm-fg-muted)' }}>
          {legend.map((l) => (
            <Box key={l.apKey} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 12, height: 12, borderRadius: '2px', background: l.color }} />
              {l.label}
            </Box>
          ))}
        </Stack>
      </Stack>
    </PmCard>
  );
}
